/**
 * Server-side Turnstile token verification (async/await).
 * @package AdvancedTrackingManager
 */

export interface VerifyResult {
  success: boolean;
  error?: string;
  errorCodes?: string[];
  data?: unknown;
}

export const verifyTokenServerSide = async (
  token: string,
  ajaxUrl: string
): Promise<VerifyResult> => {
  if (!ajaxUrl) {
    return { success: false, error: 'Server verification not configured' };
  }

  const formData = new FormData();
  formData.append('action', 'atm_verify_turnstile');
  formData.append('token', token);

  try {
    const response = await fetch(ajaxUrl, { method: 'POST', body: formData });
    const data = (await response.json()) as {
      success?: boolean;
      data?: { verified?: boolean; error?: string | string[]; 'error-codes'?: string[] };
    };
    if (data.success && data.data?.verified === true) {
      return { success: true, data: data.data };
    }
    // Always log raw response on failure so you can see exact server payload (debug + production)
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ATM Turnstile] Server verification response:', JSON.stringify(data));
    }
    const rawError = data.data?.error;
    const errorMsg = Array.isArray(rawError) ? rawError.join(', ') : (rawError ?? 'Verification failed');
    const errorCodes = Array.isArray(data.data?.['error-codes']) ? data.data['error-codes'] : undefined;
    return { success: false, error: errorMsg, errorCodes, data: data.data };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network error';
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ATM Turnstile] Server verification fetch failed:', error);
    }
    return { success: false, error: message };
  }
};
